import React from 'react';
import { Clock, Calendar } from 'lucide-react';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function ScheduleBuilder({ form, onChange }) {
  const { frequency, time, byweekday = [], bymonthday = [] } = form;

  function updateField(field, value) {
    onChange({ ...form, [field]: value });
  }

  function toggleWeekday(dayIndex) {
    const current = new Set(byweekday);
    if (current.has(dayIndex)) {
      current.delete(dayIndex);
    } else {
      current.add(dayIndex);
    }
    updateField('byweekday', Array.from(current).sort((a, b) => a - b));
  }

  function toggleMonthday(day) {
    const current = new Set(bymonthday);
    if (current.has(day)) {
      current.delete(day);
    } else {
      current.add(day);
    }
    updateField('bymonthday', Array.from(current).sort((a, b) => a - b));
  }

  return (
    <div className="space-y-4">
      {/* Frequency Selector */}
      <div>
        <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
          Frequency
        </label>
        <div className="grid grid-cols-3 gap-2">
          {['daily', 'weekly', 'monthly'].map((freq) => (
            <button
              key={freq}
              type="button"
              onClick={() => updateField('frequency', freq)}
              className={`px-4 py-2.5 sm:py-2 min-h-[44px] rounded-lg border-2 font-medium transition-colors ${
                frequency === freq
                  ? 'border-blue-600 bg-blue-50 text-blue-700'
                  : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
              }`}
            >
              {freq.charAt(0).toUpperCase() + freq.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Time Picker */}
      <div>
        <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
          <Clock className="inline w-4 h-4 mr-1" />
          Time
        </label>
        <input
          type="time"
          value={time}
          onChange={(e) => updateField('time', e.target.value)}
          className="w-full px-3 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {/* Weekly: Day Selection */}
      {frequency === 'weekly' && (
        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
            <Calendar className="inline w-4 h-4 mr-1" />
            Days of Week
          </label>
          <div className="grid grid-cols-7 gap-2">
            {DAY_NAMES.map((day, idx) => {
              const isSelected = byweekday.includes(idx);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleWeekday(idx)}
                  className={`px-1 sm:px-2 py-2.5 sm:py-3 min-h-[44px] rounded-lg border-2 font-medium text-sm transition-colors ${
                    isSelected
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {byweekday.length === 0 && (
            <p className="text-sm text-red-600 mt-1">Please select at least one day</p>
          )}
        </div>
      )}

      {/* Monthly: Day Selection */}
      {frequency === 'monthly' && (
        <div>
          <label className="block text-base sm:text-sm font-medium text-gray-700 mb-2">
            <Calendar className="inline w-4 h-4 mr-1" />
            Days of Month
          </label>
          <div className="grid grid-cols-7 gap-2">
            {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => {
              const isSelected = bymonthday.includes(day);
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => toggleMonthday(day)}
                  className={`px-1 sm:px-2 py-2 min-h-[44px] rounded-lg border-2 font-medium text-sm transition-colors ${
                    isSelected
                      ? 'border-blue-600 bg-blue-600 text-white'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}
                >
                  {day}
                </button>
              );
            })}
          </div>
          {bymonthday.length === 0 && (
            <p className="text-sm text-red-600 mt-1">Please select at least one day</p>
          )}
        </div>
      )}

      {/* Schedule Preview */}
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="text-sm font-medium text-blue-900 mb-1">Schedule Preview</div>
        <div className="text-sm text-blue-700">
          {formatSchedulePreview(form)}
        </div>
      </div>
    </div>
  );
}

function formatSchedulePreview(form) {
  const { frequency, time, byweekday = [], bymonthday = [] } = form;
  
  const timeStr = time ? formatTime(time) : '12:00 PM';
  
  if (frequency === 'weekly') {
    if (byweekday.length === 0) return `Weekly at ${timeStr} (no days selected)`;
    const days = byweekday.map(idx => DAY_NAMES[idx]).join(', ');
    return `Weekly on ${days} at ${timeStr}`;
  }
  
  if (frequency === 'monthly') {
    if (bymonthday.length === 0) return `Monthly at ${timeStr} (no days selected)`;
    const days = bymonthday.map(d => ordinal(d)).join(', ');
    return `Monthly on the ${days} at ${timeStr}`;
  }
  
  return `Daily at ${timeStr}`;
}

function formatTime(time24) {
  const [hours, minutes] = time24.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
}

function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export default ScheduleBuilder;
